import { createContext, useContext, useState, useCallback, useMemo, type ReactNode } from 'react'
import type { GridPosition } from '@/hooks/useIconGridPosition'
import type { WidgetSize } from '@/types/widget'

interface GridDragContextValue {
  isGridDragActive: boolean
  setGridDragActive: (active: boolean) => void
  hoveredGridPosition: GridPosition | null
  setHoveredGridPosition: (position: GridPosition | null) => void
  draggedItemSize: WidgetSize | 'icon' | null
  setDraggedItemSize: (size: WidgetSize | 'icon' | null) => void
}

const GridDragContext = createContext<GridDragContextValue | null>(null)

export function GridDragProvider({ children }: { children: ReactNode }) {
  const [isGridDragActive, setIsGridDragActive] = useState(false)
  const [hoveredGridPosition, setHoveredGridPositionState] = useState<GridPosition | null>(null)
  const [draggedItemSize, setDraggedItemSizeState] = useState<WidgetSize | 'icon' | null>(null)

  const setGridDragActive = useCallback((active: boolean) => {
    setIsGridDragActive(active)
    // Reset hover position when drag ends
    if (!active) {
      setHoveredGridPositionState(null)
      setDraggedItemSizeState(null)
    }
  }, [])

  const setHoveredGridPosition = useCallback((position: GridPosition | null) => {
    setHoveredGridPositionState(position)
  }, [])

  const setDraggedItemSize = useCallback((size: WidgetSize | 'icon' | null) => {
    setDraggedItemSizeState(size)
  }, [])

  const value = useMemo<GridDragContextValue>(
    () => ({
      isGridDragActive,
      setGridDragActive,
      hoveredGridPosition,
      setHoveredGridPosition,
      draggedItemSize,
      setDraggedItemSize,
    }),
    [
      isGridDragActive,
      setGridDragActive,
      hoveredGridPosition,
      setHoveredGridPosition,
      draggedItemSize,
      setDraggedItemSize,
    ]
  )

  return <GridDragContext.Provider value={value}>{children}</GridDragContext.Provider>
}

export function useGridDrag() {
  const ctx = useContext(GridDragContext)
  if (!ctx) {
    throw new Error('useGridDrag must be used within GridDragProvider')
  }
  return ctx
}
